
import React, { useState } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Search, ExternalLink } from 'lucide-react';

export const InterviewPrep = () => {
  const [searchQuery, setSearchQuery] = useState('');

  // Mock interview questions data
  const categories = [
    {
      name: 'HTML',
      questions: [
        {
          id: 1,
          question: 'Tell us about the use of the ruleset?',
          answer: 'The ruleset is used for the identification of selectors, which can be attached with other selectors. The two parts of a ruleset are:\n\nDeclaration block: contains one or more semicolon-separated declarations\nSector: indicates the HTML element needed to be styled'
        },
        {
          id: 2,
          question: 'How can we club two or more rows or columns into a single row or column in an HTML table?',
          answer: 'HTML provides two attributes for this:\n\nrowspan: Used to merge or combine two or more rows into a single row\ncolspan: Used to merge two or more columns into a single column\n\nThese attributes are used in the <td> or <th> tags to span cells across multiple rows or columns.'
        },
        {
          id: 3,
          question: 'What is the difference between HTML elements and tags?',
          answer: 'HTML elements are the complete content from start tag to end tag, including everything in between. For example, <p>This is a paragraph</p> is an HTML element.\n\nHTML tags are the markup annotations that indicate the beginning and end of an HTML element. In the above example, <p> is the opening tag and </p> is the closing tag.'
        }
      ]
    },
    {
      name: 'CSS',
      questions: [
        {
          id: 4,
          question: 'What is CSS specificity?',
          answer: 'CSS specificity is a weight that is applied to a given CSS declaration, determined by the number of each selector type in the matching selector. When multiple declarations have equal specificity, the last declaration found in the CSS is applied to the element.'
        },
        {
          id: 5,
          question: 'Explain the Box Model in CSS',
          answer: 'The CSS box model is essentially a box that wraps around every HTML element. It consists of: margins, borders, padding, and the actual content. The box model allows us to add a border around elements, and to define space between elements.'
        }
      ]
    },
    {
      name: 'JavaScript',
      questions: [
        {
          id: 6,
          question: 'What is the difference between == and === in JavaScript?',
          answer: '== (Equality Operator): Compares values for equality after converting both values to a common type. It performs type coercion.\n\n=== (Strict Equality Operator): Compares values for equality without type conversion. It returns true only if both the value and type are identical.'
        },
        {
          id: 7,
          question: 'Explain closures in JavaScript',
          answer: 'A closure is the combination of a function bundled together with references to its surrounding state (the lexical environment). In other words, a closure gives you access to an outer function\'s scope from an inner function. Closures are created every time a function is created, at function creation time.'
        }
      ]
    },
    {
      name: 'React',
      questions: [
        {
          id: 8,
          question: 'What are React Hooks?',
          answer: 'React Hooks are functions that let you "hook into" React state and lifecycle features from function components. They were introduced in React 16.8 as a way to use state and other React features without writing a class component. Some commonly used hooks include useState, useEffect, useContext, and useReducer.'
        },
        {
          id: 9,
          question: 'Explain the virtual DOM in React',
          answer: 'The Virtual DOM is a programming concept where an ideal, or "virtual", representation of a UI is kept in memory and synced with the "real" DOM by a library such as ReactDOM. This process is called reconciliation. This approach enables the declarative API of React: You tell React what state you want the UI to be in, and it makes sure the DOM matches that state.'
        }
      ]
    },
    {
      name: 'Node.js',
      questions: [
        {
          id: 10,
          question: 'What is Node.js?',
          answer: 'Node.js is an open-source, cross-platform JavaScript runtime environment that executes JavaScript code outside of a web browser. Node.js lets developers use JavaScript to write command line tools and for server-side scripting—running scripts server-side to produce dynamic web page content before the page is sent to the user\'s web browser.'
        },
        {
          id: 11,
          question: 'Explain the event loop in Node.js',
          answer: 'The Event Loop is one of the most important aspects of Node.js. Node.js is a single-threaded application, but through the event loop, it can support concurrency. The event loop allows Node.js to perform non-blocking I/O operations despite JavaScript being single-threaded. It works by offloading operations to the system kernel whenever possible.'
        }
      ]
    }
  ];

  // Filter questions based on search query
  const filteredCategories = categories.map(category => ({
    ...category,
    questions: category.questions.filter(q => 
      q.question.toLowerCase().includes(searchQuery.toLowerCase()) || 
      q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Interview Preparation</h1>
        <p className="text-bytesplash-gray-500">Practice with our curated collection of technical interview questions</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-bytesplash-gray-400" size={18} />
        <Input
          placeholder="Search interview questions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <Tabs defaultValue="questions" className="space-y-6">
        <TabsList>
          <TabsTrigger value="questions">Technical Questions</TabsTrigger>
          <TabsTrigger value="mock">Mock Interviews</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
        </TabsList>
        
        <TabsContent value="questions">
          {filteredCategories.length > 0 ? (
            <div className="space-y-4">
              {filteredCategories.map(category => (
                <Card key={category.name} className="shadow-soft overflow-hidden">
                  <CardHeader className="bg-bytesplash-light pb-3">
                    <CardTitle className="flex items-center text-lg text-bytesplash-primary">
                      {category.name}
                      <span className="ml-2 text-sm bg-white text-bytesplash-primary rounded-full px-1 py-0.5">
                        {category.questions.length}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Accordion type="multiple" className="divide-y divide-bytesplash-gray-100">
                      {category.questions.map(q => (
                        <AccordionItem key={q.id} value={`item-${q.id}`} className="border-none">
                          <AccordionTrigger className="px-6 py-4 hover:bg-bytesplash-gray-50 hover:no-underline">
                            <span className="font-medium text-left">{q.question}</span>
                          </AccordionTrigger>
                          <AccordionContent className="px-6 py-4 bg-bytesplash-gray-50 text-bytesplash-gray-800">
                            <div className="whitespace-pre-line">{q.answer}</div>
                            <div className="mt-4 flex justify-end">
                              <Button variant="link" size="sm" className="text-bytesplash-primary">
                                <span className="underline">Click Here for Reference</span>
                                <ExternalLink size={14} className="ml-1" />
                              </Button>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-soft">
              <h3 className="text-lg font-medium">No questions found</h3>
              <p className="text-bytesplash-gray-500 mt-2">Try adjusting your search query</p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="mock">
          <Card className="shadow-soft">
            <CardHeader>
              <CardTitle>Mock Interview Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="shadow-sm card-hover">
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-lg mb-2">Technical Mock Interview</h3>
                      <p className="text-bytesplash-gray-600 mb-4">Practice with an instructor in a 1:1 session focused on technical skills.</p>
                      <div className="flex justify-between items-center">
                        <span className="text-bytesplash-primary font-bold">45 minutes</span>
                        <Button className="bg-bytesplash-primary hover:bg-bytesplash-dark">Schedule</Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="shadow-sm card-hover">
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-lg mb-2">Behavioral Interview</h3>
                      <p className="text-bytesplash-gray-600 mb-4">Practice soft skills and behavioral questions for job interviews.</p>
                      <div className="flex justify-between items-center">
                        <span className="text-bytesplash-primary font-bold">30 minutes</span>
                        <Button className="bg-bytesplash-primary hover:bg-bytesplash-dark">Schedule</Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="bg-bytesplash-light rounded-lg p-4">
                  <h3 className="font-medium mb-2">Mock Interview Guidelines</h3>
                  <ul className="space-y-2 text-bytesplash-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-bytesplash-primary">•</span>
                      <span>Be prepared with a quiet environment and stable internet connection</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-bytesplash-primary">•</span>
                      <span>Dress professionally as you would for an actual interview</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-bytesplash-primary">•</span>
                      <span>Have your resume ready to share with the interviewer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-bytesplash-primary">•</span>
                      <span>You'll receive feedback immediately after the session</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="resources">
          <div className="space-y-6">
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle>Interview Preparation Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="shadow-sm">
                    <CardContent className="p-6">
                      <h3 className="font-semibold mb-3">Resume Templates</h3>
                      <p className="text-bytesplash-gray-600 text-sm mb-4">
                        Access professional resume templates designed for tech roles.
                      </p>
                      <Button variant="outline" className="w-full">Access Templates</Button>
                    </CardContent>
                  </Card>
                  
                  <Card className="shadow-sm">
                    <CardContent className="p-6">
                      <h3 className="font-semibold mb-3">Interview Guides</h3>
                      <p className="text-bytesplash-gray-600 text-sm mb-4">
                        Comprehensive guides for different technical interviews.
                      </p>
                      <Button variant="outline" className="w-full">View Guides</Button>
                    </CardContent>
                  </Card>
                  
                  <Card className="shadow-sm">
                    <CardContent className="p-6">
                      <h3 className="font-semibold mb-3">Video Tutorials</h3>
                      <p className="text-bytesplash-gray-600 text-sm mb-4">
                        Watch tutorials on answering common interview questions.
                      </p>
                      <Button variant="outline" className="w-full">Watch Videos</Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft">
              <CardHeader>
                <CardTitle>Recommended Reading</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-4 p-3 border border-bytesplash-gray-200 rounded-lg hover:bg-bytesplash-gray-50">
                    <div className="w-16 h-20 bg-bytesplash-gray-200 rounded"></div>
                    <div>
                      <h3 className="font-medium">Cracking the Coding Interview</h3>
                      <p className="text-sm text-bytesplash-gray-600">
                        Solutions to common programming interview questions.
                      </p>
                      <Button variant="link" className="p-0 h-auto text-bytesplash-primary">
                        Learn More
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 p-3 border border-bytesplash-gray-200 rounded-lg hover:bg-bytesplash-gray-50">
                    <div className="w-16 h-20 bg-bytesplash-gray-200 rounded"></div>
                    <div>
                      <h3 className="font-medium">System Design Interview</h3>
                      <p className="text-sm text-bytesplash-gray-600">
                        An insider's guide to system design interviews.
                      </p>
                      <Button variant="link" className="p-0 h-auto text-bytesplash-primary">
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
